import { AppPreloadContext } from '#app/features/app/contexts/AppPreloadContext.js';
import { useAppConfiguration } from '#app/hooks/useAppConfiguration.js';
import { useCurrentRouteKey } from '#app/hooks/useCurrentRouteKey.js';
import { useDeviceCategory } from '#app/hooks/useDeviceCategory.js';
import { usePostHog } from '#app/hooks/usePostHog.js';
import { useVisitor } from '#app/hooks/useVisitor.js';
import { type Campaign } from '#app/services/analytics/types.js';
import { findCampaign } from '#app/services/analytics/util.js';
import { logger } from '#app/services/logger.js';
import { type EventLibrary } from '#app/types/analytics/eventLibrary.js';
import { onIdle } from '#app/utilities/onIdle.js';
import { prettifyRouteKey } from '#app/utilities/prettifyRouteKey.js';
import { AnalyticsContext } from './AnalyticsContext.js';
import { type PlaywrightTrackFunction, type Track } from './types.js';
import { type AnalyticsBrowser, type ID } from '@segment/analytics-next';
import { isbot } from 'isbot';
import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const log = logger.child({
  namespace: 'AnalyticsContext',
});

const playwrightTrack: PlaywrightTrackFunction = async (
  eventName,
  properties,
) => {
  try {
    if (window.playwrightTrack) {
      await window.playwrightTrack(eventName, properties);
    }
  } catch (error) {
    log.debug({ error }, 'playwrightTrack error');
  }
};

export const AnalyticsProvider = ({
  analytics: providedAnalytics,
  children,
}: {
  readonly analytics: AnalyticsBrowser;
  readonly children: ReactNode;
}) => {
  const { playwrightTest, segmentCdnSettings, segmentCdnUrl, segmentWriteKey } =
    useAppConfiguration();
  const analytics = providedAnalytics;
  const userId = analytics?.instance?.user().id() ?? null;
  const [campaign] = useState<Campaign | null>(findCampaign());
  const currentRouteKey = useCurrentRouteKey();
  const deviceCategory = useDeviceCategory();
  const { featureFlags, posthogInstance } = usePostHog();

  // TOOD useCurrentUser
  const { currentUser, visitorTraits } = useContext(AppPreloadContext);

  const segmentContext = useMemo(() => {
    return {
      // "campaign" is included by default by analytics-next
      // https://segment.com/docs/connections/spec/common/#structure
      // However, if you analyze their implementation, it does not persist campaign between page loads.
      // https://github.com/segmentio/analytics-next/blob/8804fa820f60b9b98684ed8df7d23ed5253d510e/src/plugins/segmentio/normalize.ts#L156
      // This is why we are providing our own context with campaign that is persisted in session storage.
      campaign: campaign ?? undefined,
    };
  }, [campaign]);

  /**
   * We're updating the featureFlags to be keyed in the format $feature/<flagName>.
   * This is the format that PostHog expects to extra feature flags for things like
   * Experiments.
   *
   * See: https://posthog.com/tutorials/experiments#step-2-sending-the-right-events
   */
  const mappedFeatureFlags = useMemo(() => {
    if (!featureFlags) return {};
    const featureFlagEntries = Object.entries(featureFlags);
    const updatedFeatureFlagEntries = featureFlagEntries.map(
      ([flagKey, value]) => [`$feature/${flagKey}`, value],
    );
    return Object.fromEntries(updatedFeatureFlagEntries);
  }, [featureFlags]);

  const userIsAuthenticated = Boolean(currentUser);
  const currentUserType = currentUser?.selectedUserType;

  /*
    Adds custom posthog props to events to ensure that events are associated
    with sessions. Tip from posthog team here:
    https://contra-work.slack.com/archives/C03JJKLEC8L/p1682619134676989?thread_ts=1682603744.005469&cid=C03JJKLEC8L
  */
  const getCustomPosthogProperties = useCallback(
    (eventName: string) => {
      const calculatedProperties = posthogInstance
        ? posthogInstance._calculate_event_properties(eventName, {})
        : null;

      return calculatedProperties
        ? {
            $session_id: calculatedProperties['$session_id'],
            $user_id: calculatedProperties['$user_id'],
            $window_id: calculatedProperties['$window_id'],
          }
        : {};
    },
    [posthogInstance],
  );

  const track = useCallback<Track>(
    <T extends keyof EventLibrary>(
      eventName: T,
      properties?: (() => void) | EventLibrary[T],
      callback?: () => void,
    ) => {
      const eventProperties =
        typeof properties === 'function' ? undefined : properties;
      const eventCallback =
        typeof properties === 'function' ? properties : callback;

      if (playwrightTest) {
        playwrightTrack(eventName, eventProperties);
        eventCallback?.();
      }

      const customPosthogProperties = getCustomPosthogProperties(eventName);

      const nextProperties = {
        current_user_type:
          currentUserType && currentUserType !== '%future added value'
            ? currentUserType === 'CLIENT'
              ? 'client'
              : 'contractor'
            : undefined,
        device_category: deviceCategory,
        ...mappedFeatureFlags,
        ...customPosthogProperties,
        // Destructuring event properties after default props that need to
        // be overwritten in certain cases (ie current_user_type in paid projects)
        ...eventProperties,
        is_authenticated: userIsAuthenticated,
        is_sidenav_docked: Boolean(
          currentUser?.userInterfaceFlags?.includes('DOCKED_SIDE_NAVIGATION'),
        ),
        route_name: prettifyRouteKey(currentRouteKey),
      };

      log.debug(
        {
          context: segmentContext,
          eventName,
          properties: nextProperties,
        },
        analytics.ctx ? 'track %s' : 'track %s (buffered)',
        eventName,
      );

      void analytics
        .track(eventName, nextProperties, {
          context: segmentContext,
        })
        .finally(() => {
          eventCallback?.();
        });
    },
    [
      playwrightTest,
      getCustomPosthogProperties,
      currentUserType,
      deviceCategory,
      mappedFeatureFlags,
      userIsAuthenticated,
      currentUser?.userInterfaceFlags,
      currentRouteKey,
      segmentContext,
      analytics,
    ],
  );

  const segmentUserId = analytics.instance?.user().id() ?? null;
  const analyticsUserId = currentUser?.analyticsUserId ?? null;

  const identify = useCallback(
    (distinctId: ID, traits?: object) => {
      const customPosthogProperties = getCustomPosthogProperties('identify');
      const properties = {
        ...mappedFeatureFlags,
        ...customPosthogProperties,
        ...traits,
      };

      log.debug(
        { context: segmentContext, distinctId, properties },
        'identify',
      );

      void analytics?.identify(distinctId, properties, {
        context: segmentContext,
      });
    },
    [analytics, getCustomPosthogProperties, mappedFeatureFlags, segmentContext],
  );

  const reset = useCallback(() => {
    log.debug('reset');

    void import('posthog-js').then(({ posthog }) => {
      posthog.reset();
    });

    void analytics.reset();
  }, [analytics]);

  const reactContext = useMemo(() => {
    return {
      campaign,
      identify,
      reset,
      track,
      userId,
    };
  }, [campaign, identify, reset, track, userId]);

  const visitor = useVisitor();

  useEffect(() => {
    if (analyticsUserId && segmentUserId && segmentUserId !== analyticsUserId) {
      identify(analyticsUserId);
    }
  }, [analytics, analyticsUserId, identify, segmentUserId, visitorTraits]);

  const asnType = visitor.ipAddress?.autonomousSystem?.type ?? null;

  // TODO refactor to useEffect and added initialized state
  useEffect(() => {
    if (asnType === 'HOSTING') {
      log.warn('detected hosting; disabling analytics');

      return;
    }

    if (playwrightTest) {
      log.warn('detected playwright; disabling analytics');
    }

    onIdle(() => {
      if (analytics.ctx) {
        log.warn('analytics has already loaded');

        return;
      }

      if (isbot(navigator.userAgent)) {
        log.warn('detected bot; disabling analytics');

        return;
      }

      if (!segmentCdnUrl || !segmentCdnSettings || !segmentWriteKey) {
        log.warn('Segment disabled; Segment is not configured');

        return;
      }

      if (visitor?.impersonator) {
        log.warn('Segment disabled; impersonating');

        return;
      }

      log.info('loading Segment');

      void analytics.load(
        {
          cdnSettings: segmentCdnSettings,
          cdnURL: segmentCdnUrl,
          writeKey: segmentWriteKey,
        },
        {
          integrations: {
            'Segment.io': {
              deliveryStrategy: {
                config: {
                  size: 50,
                  timeout: 5_000,
                },
                strategy: 'batching',
              },
            },
          },
        },
      );

      void analytics.ready(async () => {
        log.info('Segment is ready');

        if (analyticsUserId) {
          log.info({ analyticsUserId }, 'Segment.identify non-anonymous user');
          identify(analyticsUserId);
        } else if (analytics?.instance?.user().id() !== visitor?.sessionId) {
          log.info(
            { sessionId: visitor?.sessionId },
            'Segment.identify first-time anonymous user',
          );
          identify(visitor?.sessionId);
        }
      });
    });
  }, [
    analytics,
    analyticsUserId,
    asnType,
    identify,
    playwrightTest,
    segmentCdnSettings,
    segmentCdnUrl,
    segmentWriteKey,
    visitor,
  ]);

  return (
    <AnalyticsContext.Provider value={reactContext}>
      {children}
    </AnalyticsContext.Provider>
  );
};
